import { useState } from "react";

interface BookNavigationProps {
  contents: any[]; // Renamed from modules to contents
  t: (key: string) => string;
  onItemSelect?: (itemId: string) => void; // Callback when an item is selected
}

export function BookNavigation({ contents, t, onItemSelect }: BookNavigationProps) {
  const [expandedContents, setExpandedContents] = useState<Set<string>>(new Set());
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  const toggleContent = (contentId: string) => {
    setExpandedContents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contentId)) {
        newSet.delete(contentId);
      } else {
        newSet.add(contentId);
      }
      return newSet;
    });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'file':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
          </svg>
        );
      case 'video':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="23 7 16 12 23 17 23 7"></polygon>
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
          </svg>
        );
      case 'practical':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
        );
      case 'quiz':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 6h18"></path>
            <path d="M7 12h10"></path>
            <path d="M7 18h6"></path>
            <path d="M3 6v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6"></path>
            <path d="M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2"></path>
          </svg>
        );
      case 'container':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"></path>
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
          </svg>
        );
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'file': return 'Tài liệu';
      case 'video': return 'Video';
      case 'practical': return 'Thực hành';
      case 'combined': return 'Tổng hợp';
      case 'quiz': return 'Bài kiểm tra';
      case 'container': return 'Phần chứa';
      default: return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'file': return 'bg-indigo-100 text-indigo-800';
      case 'video': return 'bg-red-100 text-red-800';
      case 'practical': return 'bg-orange-100 text-orange-800';
      case 'combined': return 'bg-purple-100 text-purple-800';
      case 'quiz': return 'bg-yellow-100 text-yellow-800';
      case 'container': return 'bg-teal-100 text-teal-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="md:w-1/4 bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Cấu trúc sách
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {contents.length} chương
        </p>
      </div>

      <div className="space-y-2">
        {contents.map((content: any, index: number) => (
          <div key={content.id} className="border border-gray-200 dark:border-gray-700 rounded-lg">
            {/* Content Header */}
            <div 
              className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg"
              onClick={() => toggleContent(content.id)}
            >
              <div className="flex items-center gap-2">
                <span className="text-gray-500">
                  {expandedContents.has(content.id) ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 9l6 6 6-6"></path>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18l6-6-6-6"></path>
                    </svg>
                  )}
                </span>
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {content.title || content.name}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      Chương
                    </span>
                    <span className="text-xs text-gray-500">
                      {content.children?.length || 0} phần
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Sections */}
            {expandedContents.has(content.id) && content.children && (
              <div className="border-t border-gray-200 dark:border-gray-700">
                {content.children.map((section: any, sectionIndex: number) => (
                  <div key={section.id}>
                    {/* Main Section */}
                    <div 
                      className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        selectedItem === section.id ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500' : ''
                      }`}
                      onClick={() => {
                        setSelectedItem(section.id);
                        onItemSelect?.(section.id);
                      }}
                    >
                      <div className="text-gray-400 ml-4">
                        {getTypeIcon(section?.type || 'default')}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                          {section.title || section.name}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-1 rounded-full ${getTypeColor(section?.type || 'default')}`}>
                            {getTypeLabel(section?.type || 'default')}
                          </span>
                          {section?.content?.file?.size && (
                            <span className="text-xs text-gray-500">
                              {(section?.content?.file?.size / 1024 / 1024).toFixed(1)} MB
                            </span>
                          )}
                          {section?.content_block?.content_type === 'quiz' && (
                            <span className="text-xs text-yellow-600 dark:text-yellow-400">
                              {(section?.content_block?.payload?.questions?.length || 0)} câu hỏi
                            </span>
                          )}
                          {section?.type === 'container' && section?.children && (
                            <span className="text-xs text-teal-600 dark:text-teal-400">
                              {section.children.length} phần con
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Subsections for Container Sections */}
                    {section?.type === 'container' && section?.children && section.children.length > 0 && (
                      <div className="bg-gray-50 dark:bg-gray-750">
                        {section.children.map((subSection: any, subIndex: number) => (
                          <div 
                            key={subSection.id}
                            className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
                              selectedItem === subSection.id ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500' : ''
                            }`}
                            onClick={() => {
                              setSelectedItem(subSection.id);
                              onItemSelect?.(subSection.id);
                            }}
                          >
                            <div className="text-gray-400 ml-8">
                              {getTypeIcon(subSection?.type || 'default')}
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-sm text-gray-700 dark:text-gray-300">
                                {subSection.name || subSection.title}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`text-xs px-2 py-1 rounded-full ${getTypeColor(subSection?.type || 'default')}`}>
                                  {getTypeLabel(subSection?.type || 'default')}
                                </span>
                                {subSection?.content?.file?.size && (
                                  <span className="text-xs text-gray-500">
                                    {(subSection?.content?.file?.size / 1024 / 1024).toFixed(1)} MB
                                  </span>
                                )}
                                {subSection?.content_block?.content_type === 'quiz' && (
                                  <span className="text-xs text-yellow-600 dark:text-yellow-400">
                                    {(subSection?.content_block?.payload?.questions?.length || 0)} câu hỏi
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {contents.length === 0 && (
        <div className="text-center py-8">
          <div className="text-gray-500 dark:text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
            </svg>
            <p className="text-sm">Chưa có chương nào</p>
          </div>
        </div>
      )}
    </div>
  );
}
